import type {
  SnapshotAttachmentComparisonOutcome,
  SnapshotComparison,
} from "@board-preserve/contracts";

export type ComparisonDownloadFormat = "html" | "json";

function formatAttachmentOutcome(
  value: SnapshotAttachmentComparisonOutcome | null,
): string {
  return value === null
    ? "not present"
    : `${value.status}${value.byteSize === null ? "" : ` · ${value.byteSize} bytes`}${value.sha256 === null ? "" : ` · SHA-256 ${value.sha256}`}`;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function safeTimestamp(value: string): string {
  return value.replaceAll(":", "-").replace(/\.\d{3}Z$/, "Z");
}

export function comparisonDownloadFilename(
  comparison: SnapshotComparison,
  format: ComparisonDownloadFormat,
): string {
  return `board-preserve-comparison-${safeTimestamp(comparison.from.capturedAt)}-to-${safeTimestamp(comparison.to.capturedAt)}.${format}`;
}

export function buildComparisonJson(
  comparison: SnapshotComparison,
  generatedAt = new Date().toISOString(),
): string {
  return `${JSON.stringify(
    {
      comparison,
      generatedAt,
      product: "board-preserve",
      schemaVersion: 1,
    },
    null,
    2,
  )}\n`;
}

export function buildComparisonHtml(
  comparison: SnapshotComparison,
  boardName: string,
  generatedAt = new Date().toISOString(),
): string {
  const rows = comparison.changes
    .map(
      (change) => `<tr>
          <td>${escapeHtml(change.change)}</td>
          <td>${escapeHtml(change.entityType.replaceAll("_", " "))}</td>
          <td>${escapeHtml(change.label)}</td>
          <td>${escapeHtml(change.changedFields.join(", ") || "—")}${change.attachmentOutcome ? `<br><small>From: ${escapeHtml(formatAttachmentOutcome(change.attachmentOutcome.from))}<br>To: ${escapeHtml(formatAttachmentOutcome(change.attachmentOutcome.to))}</small>` : ""}</td>
        </tr>`,
    )
    .join("\n");
  const collectionRows = Object.entries(comparison.collections)
    .filter(
      ([, counts]) =>
        counts.added + counts.changed + counts.moved + counts.removed > 0,
    )
    .map(
      ([type, counts]) => `<tr>
          <th scope="row">${escapeHtml(type.replaceAll("_", " "))}</th>
          <td>${counts.added}</td>
          <td>${counts.changed}</td>
          <td>${counts.moved}</td>
          <td>${counts.removed}</td>
        </tr>`,
    )
    .join("\n");

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'">
  <title>Board Preserve comparison</title>
  <style>
    :root { color-scheme: light; font-family: ui-sans-serif, system-ui, sans-serif; color: #172b4d; background: #f7f8fa; }
    body { max-width: 1100px; margin: 0 auto; padding: 32px 20px 56px; }
    h1 { margin-bottom: 6px; } p { color: #44546f; line-height: 1.5; }
    .summary { display: flex; flex-wrap: wrap; gap: 12px 28px; margin: 24px 0; padding: 18px; border: 1px solid #dfe1e6; border-radius: 8px; background: white; }
    .summary strong { display: block; font-size: 1.35rem; color: #0c66e4; }
    table { width: 100%; margin-top: 18px; border-collapse: collapse; background: white; }
    caption { margin-bottom: 8px; text-align: left; font-weight: 700; }
    th, td { padding: 10px 12px; border: 1px solid #dfe1e6; text-align: left; vertical-align: top; }
    thead th { background: #f1f2f4; } tbody th { text-transform: capitalize; }
    .empty { padding: 18px; border: 1px solid #dfe1e6; background: white; }
    footer { margin-top: 28px; color: #626f86; font-size: .875rem; }
  </style>
</head>
<body>
  <main>
    <h1>Board Preserve comparison</h1>
    <p><strong>${escapeHtml(boardName)}</strong><br>From ${escapeHtml(new Date(comparison.from.capturedAt).toLocaleString())} to ${escapeHtml(new Date(comparison.to.capturedAt).toLocaleString())}</p>
    <section class="summary" aria-label="Comparison summary">
      <span><strong>${comparison.totalChanges}</strong>total changes</span>
      <span><strong>${escapeHtml(comparison.from.snapshotId)}</strong>from backup ID</span>
      <span><strong>${escapeHtml(comparison.to.snapshotId)}</strong>to backup ID</span>
    </section>
    ${
      collectionRows
        ? `<table><caption>Changes by item type</caption><thead><tr><th>Item type</th><th>Added</th><th>Changed</th><th>Moved</th><th>Removed</th></tr></thead><tbody>${collectionRows}</tbody></table>`
        : ""
    }
    ${
      rows
        ? `<table><caption>Object-level changes</caption><thead><tr><th>Change</th><th>Item type</th><th>Item</th><th>Changed fields</th></tr></thead><tbody>${rows}</tbody></table>`
        : '<p class="empty">No structural differences were detected.</p>'
    }
  </main>
  <footer>Generated locally by Board Preserve at ${escapeHtml(new Date(generatedAt).toLocaleString())}. This report contains comparison metadata, not full backup contents.</footer>
</body>
</html>\n`;
}
